import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Users, ListChecks, HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useFieldGuideSearch, SearchResult } from "@/hooks/useFieldGuideSearch";
import { cn } from "@/lib/utils";

const categoryIcons: Record<SearchResult["category"], typeof FileText> = {
  producer: Users,
  crew: FileText,
  "quick-reference": ListChecks,
  troubleshooting: HelpCircle,
};

const categoryColors: Record<SearchResult["category"], string> = {
  producer: "text-primary",
  crew: "text-secondary",
  "quick-reference": "text-accent",
  troubleshooting: "text-destructive",
};

export function FieldGuideSearch() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { query, setQuery, results, isOpen, setIsOpen, categoryLabels } = useFieldGuideSearch();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setIsOpen]);

  const handleResultClick = (result: SearchResult) => {
    setIsOpen(false);
    setQuery("");
    navigate(result.path);
  };

  return (
    <div ref={containerRef} className="relative mx-auto max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={inputRef}
        placeholder="Search the Field Guide..."
        className="h-12 border-border bg-card pl-10"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
      />

      {/* Results dropdown */}
      {isOpen && query.length >= 2 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-lg border border-border bg-card shadow-lg">
          {results.length > 0 ? (
            <ul className="max-h-[400px] divide-y divide-border overflow-y-auto">
              {results.map((result, index) => {
                const Icon = categoryIcons[result.category];
                return (
                  <li key={`${result.path}-${index}`}>
                    <button
                      onClick={() => handleResultClick(result)}
                      className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <Icon
                        className={cn("mt-0.5 h-5 w-5 shrink-0", categoryColors[result.category])}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{result.title}</span>
                          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                            {categoryLabels[result.category]}
                          </span>
                        </div>
                        <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                          {result.section} — {result.description}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <p className="text-sm">No results found for "{query}"</p>
              <p className="mt-1 text-xs">Try different keywords</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
